import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, FileUpIcon, Loader2Icon, PaperclipIcon, XIcon } from 'lucide-react';
import { contact } from '../data/contact';
type Status = 'idle' | 'sending' | 'sent';
const ACCEPT = '.step,.stp,.iges,.igs,.dxf,.dwg,.x_t,.x_b,.stl,.ipt,.iam,.pdf,.png,.jpg,.jpeg,.zip';
const MAX_MB = 50;
const cardShadow = 'shadow-[0_0_0_1px_rgba(70,28,64,0.07),0_1px_2px_rgba(70,28,64,0.05),0_10px_28px_-10px_rgba(70,28,64,0.12)]';
export function HeroQuoteForm() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [contactValue, setContactValue] = useState('');
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [consent, setConsent] = useState(false);
  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`Datei ist größer als ${MAX_MB} MB – bitte als ZIP komprimieren.`);
      return;
    }
    setError(null);
    setFile(f);
  };
  const clearFile = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = contactValue.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const isPhone = /^\+?[\d\s/()-]{7,}$/.test(value);
    if (!file) return setError('Bitte laden Sie eine Projektdatei hoch.');
    if (!isEmail && !isPhone) return setError('Bitte geben Sie eine gültige E-Mail oder Telefonnummer an.');
    if (!consent) return setError('Bitte stimmen Sie der Datenschutzerklärung zu.');
    setError(null);
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 1200);
  };
  const reset = () => {
    clearFile();
    setContactValue('');
    setConsent(false);
    setStatus('idle');
  };
  return <div className="w-full max-w-[820px]" data-id="element-343" data-mp="lkyrncfpouqu">
      <div className={`rounded-[12px] bg-white p-2 text-left ${cardShadow}`} data-id="element-344" data-mp="gl451l2yyrbl">
        <AnimatePresence mode="wait" initial={false} data-id="element-345" data-mp="gj14w883rht5">
          {status === 'sent' ? <motion.div key="sent" role="status" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex min-h-[56px] items-center gap-4 px-3" data-id="element-346" data-mp="vbbe5a736xzm">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-white" data-id="element-347" data-mp="1e94fw95z51vf">
                <CheckIcon className="h-5 w-5" data-id="element-348" data-mp="19at5nxbropco" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-349" data-mp="3fzzdn6jqrb4s">
                <p className="text-[16px] font-medium text-ink" data-id="element-350" data-mp="3g0raslszlgx6">Anfrage erhalten.</p>
                <p className="text-[14px] text-muted" data-id="element-351" data-mp="3b269ej200r75">Sie bekommen Ihr Angebot innerhalb eines Werktags.</p>
              </div>
              <button type="button" onClick={reset} className="whitespace-nowrap text-[14px] text-muted underline underline-offset-4 hover:text-ink" data-id="element-352" data-mp="1qjvpgo8oix4e">
                Neue Anfrage
              </button>
            </motion.div> : <motion.form key="form" onSubmit={onSubmit} noValidate initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} aria-label="Schnellangebot anfordern" className="flex flex-col gap-2 md:flex-row md:items-stretch" data-id="element-353" data-mp="1lqapiqnizt2k">
              <input ref={inputRef} id="hero-file" type="file" accept={ACCEPT} className="sr-only" onChange={e => pickFile(e.target.files?.[0])} data-id="element-354" data-mp="12vciqd5a2sef" />

              {file ? <div className="flex h-14 min-w-0 items-center gap-2.5 rounded-[8px] bg-canvas pl-4 pr-1.5 md:w-[260px] md:shrink-0" data-id="element-355" data-mp="x36vv9tn9nlp">
                  <FileUpIcon className="h-[18px] w-[18px] shrink-0 text-ink" data-id="element-356" data-mp="o4cruydzhuy4" />
                  <div className="min-w-0 flex-1" data-id="element-357" data-mp="o6fseccrj6h3">
                    <p className="truncate text-[15px] font-medium leading-tight text-ink" data-id="element-358" data-mp="j4qdumqc0ao9">{file.name}</p>
                    <p className="text-[12px] leading-tight text-muted" data-id="element-359" data-mp="e78e9my7f7sx">{formatSize(file.size)}</p>
                  </div>
                  <button type="button" onClick={clearFile} aria-label="Datei entfernen" className="grid h-9 w-9 shrink-0 place-items-center rounded-[6px] text-muted transition-colors duration-150 hover:bg-white hover:text-ink" data-id="element-360" data-mp="9dlz9o4kirty">
                    <XIcon className="h-4 w-4" data-id="element-361" data-mp="3mmicgjyz3svf" />
                  </button>
                </div> : <label htmlFor="hero-file" title={`STEP, DXF, DWG, PDF, Foto oder Skizze · max. ${MAX_MB} MB`} onDragOver={e => {
            e.preventDefault();
            setDragging(true);
          }} onDragLeave={() => setDragging(false)} onDrop={e => {
            e.preventDefault();
            setDragging(false);
            pickFile(e.dataTransfer.files?.[0]);
          }} className={`flex h-14 cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-[8px] border border-dashed px-4 transition-colors duration-150 md:shrink-0 ${dragging ? 'border-ink bg-canvas' : 'border-tint/40 hover:border-tint hover:bg-canvas'}`} data-id="element-362" data-mp="3citj8bx44z5r">
                  <PaperclipIcon className="h-[18px] w-[18px] shrink-0 text-ink" strokeWidth={1.75} data-id="element-363" data-mp="2azhyj6tgzmbb" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-364" data-mp="2fy2q6hunoxo5">Projektdatei hochladen</span>
                </label>}

              <label htmlFor="hero-contact" className="sr-only" data-id="element-365" data-mp="231env2flrk0a">
                E-Mail oder Telefonnummer
              </label>
              <input id="hero-contact" type="text" inputMode="email" autoComplete="email" value={contactValue} onChange={e => {
            setContactValue(e.target.value);
            if (error) setError(null);
          }} placeholder="E-Mail oder Telefonnummer" className="h-14 w-full min-w-0 shrink-0 rounded-[8px] md:w-auto md:flex-1 md:shrink bg-canvas px-4 text-[16px] text-ink shadow-[inset_0_0_0_1px_rgba(70,28,64,0.06)] transition-shadow duration-150 placeholder:text-muted/70 focus:shadow-[inset_0_0_0_1px_rgba(13,13,18,0.5)] focus:outline-none" data-id="element-366" data-mp="2hozpst0qrd6m" />

              <button type="submit" disabled={status === 'sending'} className="group inline-flex h-14 items-center justify-center gap-2 whitespace-nowrap rounded-[8px] bg-ink px-6 text-[16px] font-medium text-white transition-colors duration-150 hover:bg-ink/85 disabled:cursor-wait disabled:opacity-80 md:shrink-0" data-id="element-367" data-mp="35h7tf78pjrsm">
                {status === 'sending' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-368" data-mp="3vouh3t6iu4oi" />
                    Wird gesendet
                  </> : <>
                    Kostenloses Angebot erhalten
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-369" data-mp="3mw0x5ig4qh4b" />
                  </>}
              </button>
            </motion.form>}
        </AnimatePresence>
      </div>

      {status !== 'sent' && <div className="flex justify-center px-2 pt-3.5" data-id="element-370" data-mp="nacck1fsh79n">
          <label htmlFor="hero-consent" className="flex cursor-pointer items-center gap-2.5 text-left" data-id="element-371" data-mp="3uo2coo2nk6dr">
            <input id="hero-consent" type="checkbox" checked={consent} onChange={e => {
          setConsent(e.target.checked);
          if (error) setError(null);
        }} aria-required="true" className="peer sr-only" data-id="element-372" data-mp="3pxlzn52qumrx" />
            <span aria-hidden="true" className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[4px] transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-ink/30 peer-focus-visible:ring-offset-2 ${consent ? 'bg-ink text-white' : 'bg-white shadow-[inset_0_0_0_1px_rgba(13,13,18,0.25)]'}`} data-id="element-373" data-mp="hg7o6zi6rqch">
              {consent && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-374" data-mp="2sb4zzt6jfx30" />}
            </span>
            <span className="text-[13px] leading-[1.5] text-muted" data-id="element-375" data-mp="2n9pigb3kk8pd">
              Ich akzeptiere die{' '}
              <a href={contact.privacyUrl} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2 transition-colors duration-150 hover:text-ink/70" data-id="element-376" data-mp="12u162fx54u4u">
                Datenschutzerklärung
              </a>
              .
            </span>
          </label>
        </div>}

      <div className="flex min-h-[32px] items-center justify-center px-2 pt-2" data-id="element-377" data-mp="1mfnpin024ekj">
        {error ? <p role="alert" className="text-[13px] text-[#b42318]" data-id="element-378" data-mp="1qgpqp9djb0p5">
            {error}
          </p> : <p className="text-[13px] text-muted" data-id="element-379" data-mp="1vhx01cvbs7xx">
            Vertraulich behandelt · Angebot innerhalb 1 Werktag · NDA auf Wunsch · max. {MAX_MB} MB
          </p>}
      </div>
    </div>;
}
function formatSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}